import { useCallback, useEffect, useRef, useState } from 'react';
import { Modal } from '@/components/ui/Modal';
import { aiApi } from '@/lib/api/client';
import type { LearnChain, LearnContext } from '@/learn';
import { ExplainPanel, type AiStatus } from './ExplainPanel';

export interface ExplainDialogProps {
  open: boolean;
  onClose: () => void;
  positionId: string;
  ctx: LearnContext;
  chain: LearnChain;
}

/**
 * Wraps ExplainPanel in the app's Modal and owns the AI streaming state. ExplainPanel stays pure
 * props; this is the only place that talks to aiApi.
 *
 * Callers must key this component by `positionId` (e.g. `<ExplainDialog key={positionId} .../>`)
 * so React remounts it — and its AI state resets — when the position changes, rather than
 * carrying a stale reply/error across positions.
 */
export function ExplainDialog({ open, onClose, positionId, ctx, chain }: ExplainDialogProps) {
  const [aiReply, setAiReply] = useState<string | null>(null);
  const [aiStatus, setAiStatus] = useState<AiStatus>('idle');
  const [aiError, setAiError] = useState<string | null>(null);
  const generationRef = useRef(0);

  useEffect(() => {
    generationRef.current += 1;
    setAiReply(null);
    setAiStatus('idle');
    setAiError(null);
  }, [positionId]);

  const onAskAi = useCallback(
    (prompt: string) => {
      const generation = generationRef.current;
      const isStale = () => generationRef.current !== generation;
      setAiReply(null);
      setAiError(null);
      setAiStatus('streaming');
      void (async () => {
        let reply = '';
        try {
          await aiApi.chatPosition(positionId, [{ role: 'user', content: prompt }], (delta) => {
            reply += delta;
            if (!isStale()) setAiReply(reply);
          });
          if (isStale()) return;
          setAiStatus('done');
        } catch (e) {
          if (isStale()) return;
          setAiError(e instanceof Error ? e.message : 'Chat failed');
          setAiStatus('error');
        }
      })();
    },
    [positionId],
  );

  return (
    <Modal isOpen={open} onClose={onClose} title="Explain this position" size="lg">
      <ExplainPanel ctx={ctx} chain={chain} onAskAi={onAskAi} aiReply={aiReply} aiStatus={aiStatus} aiError={aiError} />
    </Modal>
  );
}
